import { useForm, useStore } from "@tanstack/react-form";
import {
	createFileRoute,
	redirect,
	useLoaderData,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { fetchQuery, graphql, useMutation } from "react-relay";
import { toast } from "sonner";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text, TextLink } from "@/components/text";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { environment } from "@/relay/environment";
import type { cursosHotmartCourseImportMutation } from "./__generated__/cursosHotmartCourseImportMutation.graphql";
import type { cursosKiwifyCourseImportMutation } from "./__generated__/cursosKiwifyCourseImportMutation.graphql";
import type { cursosLoaderQuery } from "./__generated__/cursosLoaderQuery.graphql";
import type { cursosMemberKitCourseImportMutation } from "./__generated__/cursosMemberKitCourseImportMutation.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/cursos")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const data = await fetchQuery<cursosLoaderQuery>(
			environment,
			graphql`
      query cursosLoaderQuery($club: String!) {
        club(slug: $club) {
          leaderboardEnabled: configuration(key: "LEADERBOARD")
          communityEnabled: configuration(key: "COMMUNITY_ENABLED")
          coursePage {
            categories(status: ALL) {
              id
            }
          }
          batchJobs(status: PENDING) {
            id
            __typename
          }
          coursesEnabled: configuration(key: "COURSES_ENABLED")
        }
      }
    `,
			{
				club: params.club,
			},
		).toPromise();

		if (data?.club.coursesEnabled === "0") {
			throw redirect({
				to: "/onboarding/$club/feeds",
				params: { club: params.club },
			});
		}

		const firstCategory = data?.club.coursePage.categories.find((c) => c.id);

		if (!firstCategory) {
			throw redirect({
				to: "/onboarding/$club/feeds",
				params: { club: params.club },
			});
		}

		return {
			category: firstCategory.id,
			batchJobs: data?.club.batchJobs,
		};
	},
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { category, batchJobs } = useLoaderData({ from: Route.id });
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const { capture, trackError } = useOnboardingAnalytics("courses", clubSlug);

	const [importHotmartCourse] = useMutation<cursosHotmartCourseImportMutation>(
		graphql`
      mutation cursosHotmartCourseImportMutation($input: HotmartCourseImportInput!) {
        hotmartCourseImport(input: $input) {
          id
          club {
            ...route_batchJobs
          }
        }
      }
    `,
	);

	const [importKiwifyCourse] = useMutation<cursosKiwifyCourseImportMutation>(
		graphql`
      mutation cursosKiwifyCourseImportMutation($input: KiwifyCourseImportInput!) {
        kiwifyCourseImport(input: $input) {
          id
          club {
            ...route_batchJobs
          }
        }
      }
    `,
	);

	const [importMemberKitCourse] =
		useMutation<cursosMemberKitCourseImportMutation>(
			graphql`
      mutation cursosMemberKitCourseImportMutation($club: String!, $input: MemberKitCourseImportInput!) {
        memberKitCourseImport(club: $club, input: $input) {
          id
          club {
            ...route_batchJobs
          }
        }
      }
    `,
		);

	const [updateClub] = useMutation(graphql`
      mutation cursosOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `);

	const form = useForm({
		defaultValues: {
			courseStatus: "" as
				| ""
				| "none"
				| "hotmart"
				| "memberkit"
				| "kiwify"
				| "other",
			targetUrl: "",
			username: "",
			password: "",
			courseId: "",
			token: "",
			deviceId: "",
			courseName: "",
		},
		onSubmit: async ({ value }) => {
			const trackCompleted = () =>
				capture("completed", {
					course_platform: value.courseStatus,
					imported_course:
						value.courseStatus !== "none" && value.courseStatus !== "other",
				});

			return new Promise((resolve, reject) => {
				const finishStep = (response: unknown) => {
					updateClub({
						variables: {
							club: onboardingClub?.id ?? "",
							payload: {
								configurations: [
									{ key: "ONBOARDING_LAST_PAGE", value: "courses" },
								],
							},
						},
						onCompleted: (_clubResponse, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							trackCompleted();
							if (
								value.courseStatus !== "none" &&
								value.courseStatus !== "other"
							) {
								toast.success("Importação iniciada com sucesso!");
							}
							navigate({ to: "/onboarding/$club/feeds" });
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao salvar o progresso do onboarding.");
							reject(error);
						},
					});
				};
				if (value.courseStatus === "hotmart") {
					importHotmartCourse({
						variables: {
							input: {
								category,
								club: clubSlug,
								token: value.token,
								courseId: parseInt(value.courseId, 10),
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							finishStep(response);
						},
						onError: (error) => {
							trackError("submission", {
								error_message: error.message,
								course_platform: value.courseStatus,
							});
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else if (value.courseStatus === "kiwify") {
					importKiwifyCourse({
						variables: {
							input: {
								category,
								club: clubSlug,
								token: value.token,
								deviceId: value.deviceId,
								courseName: value.courseName,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							finishStep(response);
						},
						onError: (error) => {
							trackError("submission", {
								error_message: error.message,
								course_platform: value.courseStatus,
							});
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else if (value.courseStatus === "memberkit") {
					importMemberKitCourse({
						variables: {
							club: clubSlug,
							input: {
								category,
								loginUrl: value.targetUrl,
								targetUrl: value.targetUrl,
								username: value.username,
								password: value.password,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							finishStep(response);
						},
						onError: (error) => {
							trackError("submission", {
								error_message: error.message,
								course_platform: value.courseStatus,
							});
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else if (
					value.courseStatus === "none" ||
					value.courseStatus === "other"
				) {
					finishStep(null);
				} else {
					reject(new Error("Status do curso não selecionado"));
				}
			});
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

	const disableFields = isSubmitting || (batchJobs?.length || 0) > 0;

	return (
		<form
			className="container mx-auto p-6 max-w-2xl flex flex-col"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<Card className="p-6 gap-0">
				<div>
					<Heading>Importe seu curso</Heading>

					<Text>
						Selecione a plataforma do seu curso e insira os dados de integração
						para importar o conteúdo.
					</Text>
				</div>

				<div className="mt-6 flex flex-col gap-3">
					<form.Field
						name="courseStatus"
						validators={{
							onChange: ({ value }) =>
								!value ? { message: "Selecione uma opção" } : undefined,
						}}
					>
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>
									Você já tem cursos criados?
								</FieldLabel>
								<Select
									name={field.name}
									value={field.state.value}
									disabled={disableFields}
									onValueChange={(e) => {
										field.handleChange(
											e as
												| ""
												| "none"
												| "hotmart"
												| "memberkit"
												| "kiwify"
												| "other",
										);
									}}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Selecione uma opção" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">Não, ainda vou criar</SelectItem>
										<SelectItem value="hotmart">
											Sim, estão na Hotmart
										</SelectItem>
										<SelectItem value="memberkit">
											Sim, estão na Memberkit
										</SelectItem>
										<SelectItem value="kiwify">Sim, estão na Kiwify</SelectItem>
										<SelectItem value="other">
											Sim, estão em outra plataforma (não listada)
										</SelectItem>
									</SelectContent>
								</Select>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>
				</div>

				<form.Subscribe selector={(state) => [state.values.courseStatus]}>
					{([courseStatus]) => (
						<>
							{courseStatus === "none" && (
								<Alert className="mt-6">
									<AlertDescription>
										Ótimo! Quando você for criar seus cursos, o Scarf tem batch
										upload para facilitar a importação de múltiplos conteúdos.
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "hotmart" && (
								<Alert className="mt-6">
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>Para importar da Hotmart, siga nosso guia:</span>
											<TextLink
												href="https://ajuda.scarf.club/docs/cursos/migrando-da-hotmart/"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Como migrar da Hotmart
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "kiwify" && (
								<Alert className="mt-6">
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>Para importar da Kiwify, siga nosso guia:</span>

											<TextLink
												href="https://ajuda.scarf.club/docs/cursos/migrando-da-kiwify/"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Como migrar da Kiwify
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "other" && (
								<Alert className="mt-6">
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>
												Infelizmente ainda não temos importação automática de
												lá, mas me mande uma mensagem para que a gente possa
												ajudar nessa migração.
											</span>
											<TextLink
												href="https://wa.me/5521987293362?text=Estou%20criando%20meu%20ecossistema%20scarf%20e%20tenho%20curso%20na%20plataforma"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Falar no WhatsApp
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "memberkit" && (
								<div className="mt-6 flex flex-col gap-3">
									<form.Field
										name="targetUrl"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "URL do Curso é obrigatória" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													URL do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="https://memberkit.com.br/seu-curso"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="username"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Usuário é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>Usuário</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu.usuario"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="password"
										validators={{
											onChange: ({ value }) =>
												!value ? { message: "Senha é obrigatória" } : undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>Senha</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													type="password"
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="•••••••"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								</div>
							)}

							{courseStatus === "hotmart" && (
								<div className="mt-6 flex flex-col gap-3">
									<form.Field
										name="token"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Token de autenticação é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Token de autenticação
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													disabled={disableFields}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-token"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="courseId"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "ID do Curso é obrigatório" }
													: !/^\d+$/.test(value)
														? {
																message:
																	"ID do Curso deve ser um número válido",
															}
														: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													ID do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-token"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								</div>
							)}

							{courseStatus === "kiwify" && (
								<div className="mt-6 flex flex-col gap-3">
									<form.Field
										name="token"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Token de autenticação é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Token de autenticação
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													disabled={disableFields}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-token"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="deviceId"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "ID do Dispositivo é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													ID do Dispositivo
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-device-id"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="courseName"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Nome do Curso é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Nome do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="Nome do curso"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								</div>
							)}
						</>
					)}
				</form.Subscribe>

				<Separator className="mt-6" />

				{isSubmitting && (
					<Alert className="mt-6 bg-primary/5 border-primary!">
						<AlertDescription className="flex items-center gap-2">
							<Spinner />
							Estamos validando suas credenciais...
						</AlertDescription>
					</Alert>
				)}

				{!isSubmitting && disableFields && (
					<Alert className="mt-6 bg-primary/5 border-primary!">
						<AlertDescription className="flex flex-row gap-3 items-start justify-center">
							<Spinner className="size-4 mt-1" />
							<span className="flex-1">
								Seu curso está sendo importado...
								<br />
								<span className="text-xs/5">
									Você pode continuar navegando normalmente que te avisaremos
									quando for concluído
								</span>
							</span>
						</AlertDescription>
					</Alert>
				)}

				<form.Subscribe selector={(state) => state.values.courseStatus}>
					{(courseStatus) => (
						<form.Subscribe selector={(state) => state.isSubmitting}>
							{(isSubmitting) => (
								<div className="py-6 pb-0 flex items-center justify-between">
									<div className="flex items-start justify-start gap-3">
										<Button
											type="button"
											variant="outline"
											onClick={() =>
												navigate({
													to: "/onboarding/$club/features",
													params: { club: clubSlug },
												})
											}
											disabled={isSubmitting}
										>
											Voltar
										</Button>
									</div>

									<div className="flex items-center gap-3">
										<Button
											type="button"
											variant="ghost"
											onClick={() => {
												capture("skipped");
												navigate({
													to: "/onboarding/$club/feeds",
													params: { club: clubSlug },
												});
											}}
											disabled={isSubmitting}
										>
											Pular
										</Button>

										<Button
											type="submit"
											className="flex items-center gap-2"
											disabled={isSubmitting || !courseStatus}
										>
											{courseStatus === "none" || courseStatus === "other"
												? "Continuar"
												: "Importar"}
											{isSubmitting || (disableFields && <Spinner />)}
										</Button>
									</div>
								</div>
							)}
						</form.Subscribe>
					)}
				</form.Subscribe>
			</Card>
		</form>
	);
}
